import { Avatar, Box, Checkbox, Flex, Icon } from '@chakra-ui/react';
import { UsersRight } from '@kula-ai/icons-core';
import { KText } from '@/ui-library/components';
import { useGetCandidateSourcesQuery } from '@/store/query/candidate-sources';
import type { ReferralUser } from './markAsReferredHelpers';
// Reuse the FormBuilder checkbox styling so this matches every other
// checkbox in the form (brand-colour checked state, etc.).
import '@/components/formBuilder/styles/checkbox.css';

// Formik field that stores whether the candidate should be marked as referred.
const FIELD_NAME = 'mark_as_referred';

// The referral toggle is only meaningful when the candidate's Source is the
// "Employee Referral" type — that's the one with a referrer to credit.
const EMPLOYEE_REFERRAL_SOURCE_NAME = 'employee referral';

// The Source field is named differently across the two drawers that render
// this toggle: `ats_candidate_source` in the edit drawer, `ats_candidate_source_id`
// in the create drawer. Both store the selected source's id.
const SOURCE_FIELD_NAMES = ['ats_candidate_source', 'ats_candidate_source_id'];

type User = ReferralUser;

interface MarkAsReferredFieldProps {
  // Injected by FormBuilder's FormItem for `type: 'custom'` fields.
  formik?: {
    values?: Record<string, any>;
    setFieldValue?: (name: string, value: unknown) => void;
  };
  users?: User[];
  // Optional override for the Source field name; falls back to the known names.
  sourceFieldName?: string;
}

/**
 * "Mark this candidate as referred by …" toggle that lives under the
 * "Credited to" select. Only shown when the selected Source is "Employee
 * Referral". The referrer shown always mirrors the currently selected
 * "Credited to" user, so changing that select updates this label (and the
 * toggle hides entirely when no user is credited).
 */
export const MarkAsReferredField = ({
  formik = {},
  users = [],
  sourceFieldName,
}: MarkAsReferredFieldProps) => {
  const { values = {}, setFieldValue = () => {} } = formik;
  const { data: candidateSources } = useGetCandidateSourcesQuery({});

  // Resolve the selected Source id → name, then gate on "Employee Referral".
  const selectedSourceId = sourceFieldName
    ? values?.[sourceFieldName]
    : SOURCE_FIELD_NAMES.map((name) => values?.[name]).find(
        (value) => value !== null && value !== undefined
      );
  const selectedSource = candidateSources?.find(
    (source) => source?.id === selectedSourceId
  );
  const isEmployeeReferral =
    selectedSource?.name?.trim().toLowerCase() ===
    EMPLOYEE_REFERRAL_SOURCE_NAME;

  // Source isn't an employee referral → the toggle doesn't apply.
  if (!isEmployeeReferral) return null;

  // `credited_to_user_id` holds the selected user's id (KSelect stores the
  // scalar value, not the option object).
  const creditedToId = values?.credited_to_user_id;
  const creditedUser = users?.find((user) => user?.id === creditedToId);

  // No credited user → nothing to refer by, so don't render the toggle.
  if (!creditedUser) return null;

  const isChecked = !!values?.[FIELD_NAME];

  return (
    <Box p="4px 0px 10px 0px">
      {/* Avatar + name live OUTSIDE the Checkbox label — the same plain-flex
          context as the "Credited to" select, so the Chakra Avatar renders its
          olive initials chip (nesting it in the Chakra checkbox label broke it). */}
      <Flex alignItems="center" gap="6px">
        <Checkbox
          className="custom_checkbox"
          id={FIELD_NAME}
          isChecked={isChecked}
          onChange={(e) => setFieldValue(FIELD_NAME, e.target.checked)}
          style={{ borderRadius: '8px' }}
          alignItems="center"
        >
          <Flex alignItems="center" gap="6px" ml="2px">
            <Icon as={UsersRight} boxSize="18px" color="secondary-700" />
            <KText color="secondary-700" size="sm">
              Mark this candidate as referred by:
            </KText>
          </Flex>
        </Checkbox>
        <Avatar
          // Match the "Credited to" select: use the `avatar` field only. Some
          // users' `avatar_url` is a broken/blank image → the avatar rendered
          // empty for those names; the `avatar` field is empty → initials.
          src={creditedUser?.avatar || undefined}
          name={creditedUser?.name}
          width="16px"
          height="16px"
          minW="16px"
          minH="16px"
          flexShrink={0}
          borderRadius="3px"
          className="avatar-xs"
        />
        <KText color="secondary-900" size="sm-semi-bold">
          {creditedUser?.name}
        </KText>
      </Flex>
    </Box>
  );
};
